/** Moonlight marketplace + marketing home. */
import type { MouseEvent } from "react";

export const MARKETING_HOME = "/";

/** Inbound GPT platform page (dashboard, use cases, pricing). */
export const MARKETING_PLATFORM = "/platform";

/** Inbound GPT platform features (identity, skills, integrations, demos). */
export const MARKETING_FEATURES = "/features";

/** Product mega menu pages (dedicated feature deep-dives). */
export const MARKETING_PRODUCT = "/product";
export const MARKETING_PRODUCT_PROFILE = "/product/profile";
export const MARKETING_PRODUCT_RESPONSIBILITIES = "/product/responsibilities";
export const MARKETING_PRODUCT_TRAINING = "/product/training";
export const MARKETING_PRODUCT_TOOLS = "/product/tools";
export const MARKETING_PRODUCT_INTEGRATIONS = "/product/integrations";

/** Custom agent / role development request form. */
export const MARKETING_CUSTOM_AGENT_DEVELOPMENT = "/custom-agent-development";

/** Standalone unit-pricing calculator. */
export const MARKETING_PRICING = "/pricing";
/** @deprecated Use MARKETING_PRICING — pricing moved off Platform. */
export const MARKETING_PLATFORM_PRICING = "/pricing";

/** Browse and clone marketplace agents. */
export const MARKETING_CLONE_AGENTS = "/agents";

/** Custom hire — build an agent from scratch. */
export const MARKETING_BUILD_CUSTOM = "/agents/hire/custom";

/** Fresh custom hire — clears stale website/onboarding cache via ?new=1. */
export const MARKETING_BUILD_CUSTOM_NEW = "/agents/hire/custom?new=1";

/**
 * SiteGlue onboard workspace — primary Get Started Free / marketing conversion path.
 * Classic Trish hire remains at MARKETING_BUILD_CUSTOM for legacy flows.
 */
export const SITEGLUE_ONBOARD_WORKSPACE = "/onboard";
export const SITEGLUE_ONBOARD_WORKSPACE_NEW = "/onboard?new=1";

/** Agent templates browse (formerly case studies landing). */
export const MARKETING_CASE_STUDIES = "/case-studies";
export const MARKETING_AGENT_TEMPLATES = "/agents";

/** Customer stories / social proof. */
export const MARKETING_CUSTOMER_STORIES = "/customer-stories";

/** @deprecated Use MARKETING_HOME */
export const INBOUND_GPT_BASE = MARKETING_HOME;

/** Product + Resources subpages at site root. */
export const INBOUND_GPT_SUBROUTES = [
  "contact",
  "case-studies",
  "customer-stories",
  "custom-agent-development",
  "how-it-works",
  "platform",
  "product",
  "faq",
  "documentation",
  "blog",
  "announcements",
  "help-center",
  "privacy-policy",
  "terms-of-service",
  "google-api-user-data",
  "api-reference",
  "ai-summary",
  "resources",
  "directory",
  "audits",
  "team-siteglue",
  "hire-trish",
  "services",
  "dental",
  "physical-therapy",
  "legal",
  "home-services",
  "healthcare",
  "real-estate",
  "financial-services",
  "education",
  "retail",
  "nonprofits-and-churches",
  "solutions",
  "ai-receptionist",
  "ai-phone-system",
  "ai-employees",
  "ai-for-churches",
  "ai-for-government",
  "ai-for-nonprofits",
  "human-in-the-loop",
  "team-handoff",
  "team-takeover",
  "phone-handling-rules",
  "smith-ai-alternative",
  "welcome",
  "get-started",
] as const;

export type MarketingNavItem = { label: string; href: string };

/** Features / Product menu — dedicated product pages + overview. */
export const INBOUND_GPT_FEATURE_NAV: MarketingNavItem[] = [
  { label: "All Features", href: "/features" },
  { label: "Inbound GPT Agent Profile", href: MARKETING_PRODUCT_PROFILE },
  { label: "Inbound GPT Agent Responsibilities", href: MARKETING_PRODUCT_RESPONSIBILITIES },
  { label: "Inbound GPT Agent Tools", href: MARKETING_PRODUCT_TOOLS },
  { label: "Inbound GPT Agent Training", href: MARKETING_PRODUCT_TRAINING },
  { label: "Integrations", href: MARKETING_PRODUCT_INTEGRATIONS },
  { label: "All Inbound GPT Agents", href: "/features#agents" },
];

/** Legacy feature-page hash anchors → current section ids / public pages. */
export const FEATURES_SECTION_HASH_ALIASES: Record<string, string> = {
  skills: "responsibilities",
  hitl: "human-in-the-loop",
  "human-handoff": "human-in-the-loop",
  "team-takeover": "human-in-the-loop",
  "team-handoff": "human-in-the-loop",
};

export const MARKETING_PRODUCT_NAV: MarketingNavItem[] = [
  { label: "Platform", href: "/platform" },
  { label: "Pricing", href: "/pricing" },
  { label: "Features", href: "/features" },
  { label: "Agent Templates", href: "/agents" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "FAQ", href: "/faq" },
];

/** SiteGlue footer / sitemap product links — same hierarchy as page story. */
export const SITEGLUE_PRODUCT_NAV: MarketingNavItem[] = [
  { label: "SiteGlue Agent", href: "/ai-employees" },
  { label: "How It Works", href: "/how-it-works" },
  { label: "Directory", href: "/directory" },
  { label: "Pricing", href: "/pricing" },
  { label: "Resources", href: "/resources" },
  { label: "Platform", href: "/platform" },
  { label: "Features", href: "/features" },
  { label: "Live Agents", href: "/agents" },
  { label: "FAQ", href: "/faq" },
];

export const MARKETING_RESOURCES_NAV: MarketingNavItem[] = [
  { label: "All Resources", href: "/resources" },
  { label: "Customer Stories", href: "/customer-stories" },
  { label: "Documentation", href: "/documentation" },
  { label: "Blog", href: "/blog" },
  { label: "Announcements", href: "/announcements" },
  { label: "Help Center", href: "/help-center" },
  { label: "API Reference", href: "/api-reference" },
  { label: "AI Summary", href: "/ai-summary" },
];

export const MARKETING_SOLUTIONS_NAV: MarketingNavItem[] = [
  { label: "Online Patient Coordinator", href: "/online-patient-coordinator" },
  { label: "Scheduling Specialist", href: "/scheduling-specialist" },
  { label: "Online Support Specialist", href: "/online-support-agent" },
  { label: "Front Desk Receptionist", href: "/front-desk-receptionist" },
  { label: "New Student Advisor", href: "/new-student-advisor" },
  { label: "Online Sales Representative", href: "/online-sales-rep" },
];

function normalizePath(href: string): string {
  return href.replace(/\/$/, "") || "/";
}

function isInboundSubroute(path: string): boolean {
  for (const slug of INBOUND_GPT_SUBROUTES) {
    if (path === `/${slug}` || path.startsWith(`/${slug}/`)) {
      return true;
    }
  }
  return false;
}

/** Strip legacy /inbound-gpt prefix from hrefs. */
export function stripLegacyInboundPrefix(href: string): string {
  if (!href.startsWith("/inbound-gpt")) return href;
  const rest = href.slice("/inbound-gpt".length);
  return rest.length ? rest : "/";
}

/**
 * Resolve marketing links to root routes (legacy /inbound-gpt paths normalized).
 */
export function inboundGptHref(href: string): string {
  if (
    href.startsWith("http") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    href.startsWith("#")
  ) {
    return href;
  }

  href = stripLegacyInboundPrefix(href);

  if (href.startsWith("/get-started")) {
    return "/sign-up";
  }

  const passthroughPrefixes = [
    "/about",
    "/agents",
    "/dental",
    "/physical-therapy",
    "/legal",
    "/home-services",
    "/ai-receptionist",
    "/ai-phone-system",
    "/ai-employees",
    "/ai-for-churches",
    "/ai-for-government",
    "/ai-for-nonprofits",
    "/human-in-the-loop",
    "/team-handoff",
    "/team-takeover",
    "/phone-handling-rules",
    "/smith-ai-alternative",
    "/employers",
    "/features",
    "/product",
    "/custom-agent-development",
    "/pricing",
    "/sign-in",
    "/sign-up",
    "/platform",
    "/how-it-works",
    "/directory",
    "/resources",
    "/case-studies",
    "/customer-stories",
    "/faq",
    "/documentation",
    "/blog",
    "/announcements",
    "/help-center",
    "/privacy-policy",
    "/terms-of-service",
    "/google-api-user-data",
    "/api-reference",
    "/ai-summary",
    "/online-patient-coordinator",
    "/scheduling-specialist",
    "/online-support-agent",
    "/front-desk-receptionist",
    "/new-student-advisor",
    "/online-sales-rep",
  ];
  if (passthroughPrefixes.some((p) => href === p || href.startsWith(`${p}/`) || href.startsWith(`${p}#`))) {
    return href;
  }

  const normalized = normalizePath(href.split("#")[0] ?? href);
  const hash = href.includes("#") ? href.slice(href.indexOf("#")) : "";

  if (isInboundSubroute(normalized)) {
    return `${normalized}${hash}`;
  }

  if (href === "/" || href === MARKETING_HOME) {
    return MARKETING_HOME;
  }

  if (href.startsWith("/#")) {
    return `${MARKETING_HOME}${href.slice(1)}`;
  }

  if (href.startsWith("/")) {
    return `${href.split("#")[0]}${hash}`;
  }

  return href;
}

export function scrollToInboundSection(sectionId: string) {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
}

/** Smooth-scroll when the link target is on the current page. */
export function handleMarketingAnchorClick(
  e: MouseEvent<HTMLAnchorElement>,
  href: string,
  pathname: string,
) {
  const resolved = inboundGptHref(href);
  const hashIndex = resolved.indexOf("#");
  if (hashIndex === -1) return;

  const path = resolved.slice(0, hashIndex) || "/";
  const sectionId = resolved.slice(hashIndex + 1);
  if (pathname === path) {
    e.preventDefault();
    scrollToInboundSection(sectionId);
  }
}

/** Widget script base — same origin on www.easygpt.builders, app subdomain in dev. */
export function widgetScriptUrl(widgetId: string): string {
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : process.env.NEXT_PUBLIC_APP_URL ?? "https://www.easygpt.builders";
  return `${origin.replace(/\/$/, "")}/w.js?id=${widgetId}`;
}
